import { useState } from "react";
import { Camera, Paperclip, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { Confirm, FormDialog } from "@/components/kids/kidsUi";
import { Checkbox } from "@/components/ui/checkbox";
import { ACCEPT, checkFile, uploadFile } from "@/lib/upload13";
import { Thumb, uploadMany } from "@/components/inventory/util";

const IMG = ".jpg,.jpeg,.png,.heic,.heif";

function Photos({ base, item, keep, setKeep, adds, setAdds }) {
  const count = keep.length + adds.length;
  const pick = (e) => {
    const fs = [...e.target.files];
    e.target.value = "";
    if (count + fs.length > 3) { toast.error("Up to 3 photos per item."); return; }
    const bad = fs.map(checkFile).find(Boolean);
    if (bad) toast.error(bad); else setAdds([...adds, ...fs]);
  };
  return (
    <div data-testid="item-photos">
      <p className="q-label mb-2">Photos ({count}/3)</p>
      <div className="flex flex-wrap gap-2">
        {item && keep.map((p) => (
          <span key={p.id} className="relative">
            <Thumb url={`${base}/items/${item.id}/photos/${p.id}`} alt={item.name} testid={`item-photo-${p.id}`} />
            <button className="absolute -right-2 -top-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-q-bg text-q-text shadow" aria-label="Remove photo" onClick={() => setKeep(keep.filter((x) => x.id !== p.id))} data-testid={`item-photo-remove-${p.id}`}><X className="h-4 w-4" /></button>
          </span>
        ))}
        {adds.map((f, n) => <span key={n} className="inline-flex h-20 max-w-[140px] items-center rounded-xl bg-q-raised px-2 text-xs text-q-text">{f.name}<button className="ml-1 min-h-[44px] min-w-[44px]" aria-label="Remove" onClick={() => setAdds(adds.filter((_, i) => i !== n))}><X className="h-4 w-4" /></button></span>)}
      </div>
      <label className="q-btn q-btn-secondary mt-2 min-h-[44px] cursor-pointer" data-testid="item-photo-add">
        <Camera className="h-4 w-4" /> Add photo
        <input type="file" accept={IMG} capture="environment" multiple className="hidden" onChange={pick} data-testid="item-photo-input" />
      </label>
    </div>
  );
}

export default function ItemDialog({ base, d, item, preset, onClose, onSaved, quick, locked }) {
  const [f, setF] = useState({
    name: item?.name || preset?.name || "", room_id: item?.room_id || preset?.room_id || d.rooms[0]?.id, category: item?.category || "Other",
    value: item?.value ?? preset?.value ?? "", quantity: item?.quantity || 1, purchase_date: item?.purchase_date || "", brand_model: item?.brand_model || "",
    serial: item?.serial || "", notes: item?.notes || "", valuable: !!item?.valuable, valuable_type: item?.valuable_type || d.valuable_types[0],
  });
  const [keep, setKeep] = useState(item?.photos || []);
  const [adds, setAdds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    if (!keep.length && !adds.length) { toast.error("Add at least one photo."); return; }
    setBusy(true);
    try {
      const ids = await uploadMany(base, adds);
      const body = { ...f, value: f.value === "" ? null : Number(f.value), quantity: Number(f.quantity) || 1, purchase_date: f.purchase_date || null,
        photo_upload_ids: ids, remove_photo_ids: (item?.photos || []).filter((p) => !keep.some((k) => k.id === p.id)).map((p) => p.id) };
      const { data } = item ? await api.put(`${base}/items/${item.id}`, body) : await api.post(`${base}/items`, body);
      toast.success(item ? "Saved" : `Added to ${data.room}`); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const receipt = async (e) => {
    const x = e.target.files[0]; e.target.value = "";
    const bad = x && checkFile(x); if (bad) { toast.error(bad); return; }
    try { const uid = await uploadFile(base, x); const { data } = await api.post(`${base}/items/${item.id}/receipt`, { upload_id: uid }); toast.success("Receipt filed in Documents"); onSaved(data, true); } catch (er) { toast.error(errMsg(er)); }
  };
  const detach = () => api.delete(`${base}/items/${item.id}/receipt/${item.receipt.id}`).then(({ data }) => { toast.success("Removed from this item. It stays in Documents."); onSaved(data, true); }).catch((e) => toast.error(errMsg(e)));
  const remove = () => api.delete(`${base}/items/${item.id}`).then(() => { toast.success("Item deleted"); onSaved(null); }).catch((e) => toast.error(errMsg(e)));
  const inp = "q-input min-h-[44px] w-full";
  return (
    <FormDialog open onClose={onClose} title={item ? item.name : quick ? "Snap a new purchase" : "Add item"} testid="item-dialog">
      <div className="space-y-3">
        <Photos base={base} item={item} keep={keep} setKeep={setKeep} adds={adds} setAdds={setAdds} />
        <Field label="Name"><input className={inp} maxLength={80} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="item-name" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Room"><select className={inp} value={f.room_id} onChange={(e) => set("room_id", e.target.value)} data-testid="item-room">{d.rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
          <Field label="Value ($)"><input className={inp} type="number" min="0" step="0.01" inputMode="decimal" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="item-value" /></Field>
        </div>
        {!quick && <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Category"><select className={inp} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="item-category">{d.categories.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label="Quantity"><input className={inp} type="number" min="1" max="999" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="item-quantity" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Purchase date"><input className={inp} type="date" value={f.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} data-testid="item-date" /></Field>
            <Field label="Brand / model"><input className={inp} maxLength={80} value={f.brand_model} onChange={(e) => set("brand_model", e.target.value)} data-testid="item-brand" /></Field>
          </div>
          <Field label="Serial number"><input className={inp} maxLength={60} value={f.serial} onChange={(e) => set("serial", e.target.value)} data-testid="item-serial" /></Field>
          <label className="flex min-h-[44px] items-center gap-3 text-sm text-q-text"><Checkbox className="h-6 w-6" checked={f.valuable} onCheckedChange={(v) => set("valuable", !!v)} data-testid="item-valuable" /> Valuable</label>
          {f.valuable && <Field label="Valuable type"><select className={inp} value={f.valuable_type} onChange={(e) => set("valuable_type", e.target.value)} data-testid="item-valuable-type">{d.valuable_types.map((c) => <option key={c}>{c}</option>)}</select></Field>}
          <Field label="Notes"><textarea className="q-input min-h-[64px] w-full" maxLength={1000} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="item-notes" /></Field>
        </>}
        {item && (
          <div className="rounded-xl bg-q-raised p-3" data-testid="item-receipt">
            <p className="q-label mb-1">Receipt</p>
            {item.receipt ? (
              <div className="flex items-center gap-2 text-sm"><span className="min-w-0 flex-1 truncate">{item.receipt.title}</span><button className="q-btn q-btn-ghost min-h-[44px]" disabled={locked} onClick={detach} data-testid="item-receipt-remove">Remove</button></div>
            ) : (
              <label className={`q-btn q-btn-secondary min-h-[44px] ${locked ? "pointer-events-none opacity-50" : "cursor-pointer"}`} data-testid="item-receipt-add"><Paperclip className="h-4 w-4" /> Attach receipt
                <input type="file" accept={ACCEPT} className="hidden" disabled={locked} onChange={receipt} data-testid="item-receipt-input" /></label>
            )}
            <p className="mt-1 text-xs text-q-muted">Filed once in Documents → Warranties &amp; Manuals.</p>
          </div>
        )}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy || locked || !f.name.trim()} onClick={save} data-testid="item-save-btn">{busy ? "Saving…" : "Save"}</button>
        {item && <button className="q-btn q-btn-ghost min-h-[44px] w-full" disabled={locked} onClick={() => setDel(true)} data-testid="item-delete-btn"><Trash2 className="h-4 w-4" /> Delete item</button>}
      </div>
      <Confirm open={del} onClose={() => setDel(false)} title={`Delete ${item?.name}?`} body="Its photos go too. A receipt stays in Documents." confirmLabel="Delete" onConfirm={remove} testid="item-delete-confirm" />
    </FormDialog>
  );
}
